import React, { useEffect, useMemo, useRef, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    getSubscribe,
    setCabinetWalletSubscribeModalStatus
} from "../../../store/cabinet/action";

import CryptoIcon from "../../_UI/cryptoIcon";

const getSubscribeData = (data) => {
    return (
        data?.response ||
        data?.data?.response ||
        data?.data ||
        data ||
        {}
    );
};

const formatNumber = (value, maxDigits = 8) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0";

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxDigits,
    }).format(numberValue);
};

const formatPrice = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0";

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(numberValue);
};

const getPlanLabel = (durationMonths, t) => {
    const months = Number(durationMonths);

    if (months === 12) return t("1 год");
    if (months === 6) return t("6 месяцев");
    if (months === 3) return t("3 месяца");
    if (months === 1) return t("1 месяц");

    return `${months} ${t("месяцев")}`;
};

const getNetworkLabel = (asset) => {
    const assetType = asset?.type;
    const networkCode = asset?.network?.code;

    if (assetType && assetType !== "native") {
        return assetType.toUpperCase();
    }

    if (networkCode) {
        return networkCode.toUpperCase();
    }

    return "";
};

const getPaymentSourcePriority = (source) => {
    const code = source?.code?.toUpperCase();
    const networkLabel = source?.networkLabel?.toUpperCase();

    if (code === "USDT" && networkLabel === "TRC20") return 1;
    if (code === "USDT" && networkLabel === "TRC-20") return 1;
    if (code === "USDT" && networkLabel === "BEP20") return 2;
    if (code === "USDT" && networkLabel === "ERC20") return 3;
    if (code === "USDT") return 4;

    return 10;
};

const normalizePlans = (data) => {
    const plans = data?.subscription?.plans || [];

    if (!Array.isArray(plans)) return [];

    return plans.map((plan) => ({
        id: plan.id,
        durationMonths: plan.duration_months,
        price: plan.price,
    }));
};

const normalizePaymentSources = (data) => {
    const wallets = data?.wallets || [];

    if (!Array.isArray(wallets)) return [];

    const sources = wallets.flatMap((wallet) => {
        return (wallet?.balances || []).map((balance) => {
            const asset = balance?.asset;
            const currency = asset?.currency;
            const network = asset?.network;
            const networkLabel = getNetworkLabel(asset);

            return {
                id: `${wallet?.id || wallet?.network_type || "wallet"}-${asset?.id}-${asset?.type}`,
                walletId: wallet?.id,
                assetId: asset?.id,
                currencyId: currency?.id,
                networkId: network?.id,
                code: currency?.code || "",
                name: currency?.name || currency?.code || "",
                networkLabel,
                networkName: network?.name || "",
                balance: balance?.amount_decimal || "0",
                precision: currency?.precision || asset?.decimals || 8,
                isStub: wallet?.is_stub,
            };
        });
    });

    return sources.sort((a, b) => {
        return getPaymentSourcePriority(a) - getPaymentSourcePriority(b);
    });
};

const CabinetWalletSubscribeModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const dropdownRef = useRef(null);

    const modalStatus = useSelector(store => store.cabinet.cabinetWalletSubscribeModalStatus);
    const getSubscribeStatus = useSelector(store => store.cabinet.getSubscribeStatus);

    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [selectedPaymentSource, setSelectedPaymentSource] = useState(null);
    const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);

    const subscribeData = useMemo(() => {
        return getSubscribeData(getSubscribeStatus);
    }, [getSubscribeStatus]);

    const plans = useMemo(() => {
        return normalizePlans(subscribeData);
    }, [subscribeData]);

    const paymentSources = useMemo(() => {
        return normalizePaymentSources(subscribeData);
    }, [subscribeData]);

    const selectedPlan = useMemo(() => {
        return plans.find(plan => Number(plan.id) === Number(selectedPlanId)) || plans[0] || null;
    }, [plans, selectedPlanId]);

    const selectedSourceBalance = Number(selectedPaymentSource?.balance || 0);
    const priceNumber = Number(selectedPlan?.price || 0);

    const currency = "USDT";

    const hasPaymentDropdown = paymentSources.length > 1;

    const isPayDisabled =
        !selectedPlan?.id ||
        !selectedPaymentSource?.id ||
        priceNumber <= 0 ||
        selectedSourceBalance < priceNumber;

    const priceLabel = `${formatPrice(selectedPlan?.price)} ${currency}`;

    const balanceLabel = selectedPaymentSource
        ? `${formatNumber(selectedPaymentSource.balance, selectedPaymentSource.precision)} ${selectedPaymentSource.code}`
        : `0 ${currency}`;

    const closeModal = () => {
        dispatch(setCabinetWalletSubscribeModalStatus(false));
        setIsPaymentDropdownOpen(false);
    };

    const planSelectHandler = (planId) => {
        setSelectedPlanId(planId);
    };

    const paymentDropdownToggleHandler = () => {
        if (!hasPaymentDropdown) return;

        setIsPaymentDropdownOpen(prev => !prev);
    };

    const paymentSourceSelectHandler = (source) => {
        setSelectedPaymentSource(source);
        setIsPaymentDropdownOpen(false);
    };

    const payHandler = () => {
        if (isPayDisabled) return;

        const payload = {
            subscription_id: subscribeData?.subscription?.id,
            plan_id: selectedPlan.id,
            duration_months: selectedPlan.durationMonths,
            price: selectedPlan.price,
            currency,
            wallet_id: selectedPaymentSource.walletId,
            asset_id: selectedPaymentSource.assetId,
            currency_id: selectedPaymentSource.currencyId,
            network_id: selectedPaymentSource.networkId,
        };

        console.log("Subscribe payment payload:", payload);

        // dispatch(paySubscribe(payload));
    };

    useEffect(() => {
        if (modalStatus) {
            dispatch(getSubscribe());
        }
    }, [dispatch, modalStatus]);

    useEffect(() => {
        if (!modalStatus) {
            setSelectedPlanId(null);
            setSelectedPaymentSource(null);
            setIsPaymentDropdownOpen(false);
            return;
        }

        if (plans.length && !selectedPlanId) {
            setSelectedPlanId(plans[0].id);
        }
    }, [modalStatus, plans, selectedPlanId]);

    useEffect(() => {
        if (!modalStatus) return;

        if (paymentSources.length && !selectedPaymentSource) {
            setSelectedPaymentSource(paymentSources[0]);
        }
    }, [modalStatus, paymentSources, selectedPaymentSource]);

    useEffect(() => {
        const clickOutsideHandler = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsPaymentDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", clickOutsideHandler);

        return () => {
            document.removeEventListener("mousedown", clickOutsideHandler);
        };
    }, []);

    return (
        <Popup
            open={modalStatus}
            modal
            nested
            closeOnDocumentClick
            onClose={closeModal}
            className="cabinetWalletSubscribePopup"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={Styles.modalCloseBtn}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.title}>
                            {t("Активировать аккаунт")}
                        </div>

                        <div className={Styles.note}>
                            {t("Для активации нужен платёж за подписку, чтобы открыть полный доступ.")}
                        </div>
                    </div>

                    <div className={Styles.subscribeBody}>
                        <div className={Styles.sectionTitle}>
                            {t("Детали подписки")}
                        </div>

                        <div className={Styles.subscriptionCard}>
                            <div className={Styles.subscriptionTop}>
                                <div className={Styles.subscriptionInfo}>
                                    <div className={Styles.subscriptionIcon}>
                                        {/* image here */}
                                    </div>

                                    <div className={Styles.subscriptionText}>
                                        <div className={Styles.subscriptionName}>
                                            {subscribeData?.subscription?.name || t("Активация аккаунта Kairo")}
                                        </div>

                                        <div className={Styles.subscriptionNote}>
                                            {t("Плата за активацию")}
                                        </div>
                                    </div>
                                </div>

                                <div className={Styles.priceBox}>
                                    <div className={Styles.priceValue}>
                                        {priceLabel}
                                    </div>

                                    <div className={Styles.priceLabel}>
                                        {t("Стоимость подписки")}
                                    </div>
                                </div>
                            </div>

                            <div className={Styles.plansWrap}>
                                {plans.map((plan) => {
                                    const isActive = Number(selectedPlan?.id) === Number(plan.id);

                                    return (
                                        <button
                                            type="button"
                                            className={`${Styles.planBtn} ${isActive ? Styles.planBtnActive : ""}`}
                                            key={plan.id}
                                            onClick={() => planSelectHandler(plan.id)}
                                        >
                                            {getPlanLabel(plan.durationMonths, t)}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className={Styles.featuresList}>
                                <div className={Styles.featureRow}>
                                    <span className={Styles.checkIcon} />
                                    <span>{t("Полный доступ ко всем возможностям платформы Kairo")}</span>
                                </div>

                                <div className={Styles.featureRow}>
                                    <span className={Styles.checkIcon} />
                                    <span>{t("Возможности пополнения")}</span>
                                </div>

                                <div className={Styles.featureRow}>
                                    <span className={Styles.checkIcon} />
                                    <span>{t("Доход через партнёрскую программу")}</span>
                                </div>
                            </div>
                        </div>

                        <div className={Styles.sectionTitle}>
                            {t("Источник оплаты")}
                        </div>

                        <div
                            className={Styles.dropdownWrap}
                            ref={dropdownRef}
                        >
                            <button
                                type="button"
                                className={`
                                    ${Styles.dropdownBtn}
                                    ${isPaymentDropdownOpen ? Styles.dropdownBtnActive : ""}
                                    ${!hasPaymentDropdown ? Styles.dropdownBtnSingle : ""}
                                `}
                                onClick={paymentDropdownToggleHandler}
                                disabled={!paymentSources.length}
                            >
                                <div className={Styles.dropdownAsset}>
                                    <CryptoIcon
                                        code={selectedPaymentSource?.code}
                                        className={Styles.currencyIcon}
                                    />

                                    <div className={Styles.dropdownAssetText}>
                                        <div className={Styles.dropdownAssetName}>
                                            {selectedPaymentSource?.code || t("Выберите актив")}
                                        </div>

                                        <div className={Styles.dropdownAssetCode}>
                                            {selectedPaymentSource?.networkLabel || ""}
                                        </div>
                                    </div>
                                </div>

                                <div className={Styles.dropdownBalance}>
                                    {t("Баланс")}: {balanceLabel}
                                </div>

                                {hasPaymentDropdown && (
                                    <div className={Styles.dropdownArrow} />
                                )}
                            </button>

                            {isPaymentDropdownOpen && hasPaymentDropdown && (
                                <div className={Styles.dropdownList}>
                                    <div className={Styles.dropdownListScroll}>
                                        {paymentSources.map((source) => {
                                            const isActive = selectedPaymentSource?.id === source.id;

                                            return (
                                                <button
                                                    type="button"
                                                    className={`
                                                        ${Styles.dropdownListItem}
                                                        ${isActive ? Styles.dropdownListItemActive : ""}
                                                    `}
                                                    key={source.id}
                                                    onClick={() => paymentSourceSelectHandler(source)}
                                                >
                                                    <CryptoIcon
                                                        code={source.code}
                                                        className={Styles.currencyIcon}
                                                    />

                                                    <div className={Styles.dropdownAssetText}>
                                                        <div className={Styles.dropdownAssetName}>
                                                            {source.code}
                                                        </div>

                                                        <div className={Styles.dropdownAssetCode}>
                                                            {source.networkLabel}
                                                        </div>
                                                    </div>

                                                    <div className={Styles.dropdownListBalance}>
                                                        {formatNumber(source.balance, source.precision)} {source.code}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {selectedPaymentSource && selectedSourceBalance < priceNumber && (
                            <div className={Styles.balanceWarning}>
                                {t("Недостаточно средств для оплаты выбранной подписки")}
                            </div>
                        )}

                        <button
                            type="button"
                            className={Styles.payBtn}
                            onClick={payHandler}
                            disabled={isPayDisabled}
                        >
                            {t("Оплатить")} {priceLabel}
                        </button>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetWalletSubscribeModal;
